import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Event as EventIcon,
  Send as SendIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axiosInstance from '../config/axios';

const EventSuggestionIconContainer = styled(Box)(({ theme, showTooltip }) => ({
  position: 'fixed',
  bottom: '420px', // Positioned at the old ReportIcon position
  right: '20px',
  zIndex: showTooltip ? 9990 : 9998, // Lower z-index when tooltip is shown
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const EventSuggestionIconOverlay = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)', // Blue gradient
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(66, 165, 245, 0.3)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 25px rgba(66, 165, 245, 0.5)',
  },
}));

const EventSuggestionTooltip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '10px',
  right: '80px',
  width: '280px',
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', // Blue gradient
  border: '2px solid #42a5f5', // Blue border
  borderRadius: '12px',
  padding: theme.spacing(2),
  zIndex: 10014,
  boxShadow: '0 12px 40px rgba(66, 165, 245, 0.4)',
  backdropFilter: 'blur(10px)',
  animation: 'tooltipSlideIn 0.3s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: '20px',
    right: '-8px',
    width: 0,
    height: 0,
    borderLeft: '8px solid #42a5f5',
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
  },
  '@keyframes tooltipSlideIn': {
    '0%': {
      opacity: 0,
      transform: 'translateX(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
}));

const EventSuggestionIcon = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    event_type: 'cybersecurity',
    target_audience: 'bevoelkerung',
    location: '',
    url: '',
    organizer: '',
    organizer_logo: '',
    is_all_day: false,
    reason: '', // Why this event should be added
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const eventTypes = [
    { value: 'cybersecurity', label: 'Cybersecurity' },
    { value: 'conference', label: 'Konferenz' },
    { value: 'training', label: 'Schulung' },
    { value: 'webinar', label: 'Webinar' },
    { value: 'digitale_sprechstunde', label: 'Digitale Sprechstunde' },
    { value: 'podcast', label: 'Podcast' },
    { value: 'videocast', label: 'Videocast' },
    { value: 'sonstige', label: 'Sonstige' },
  ];

  const targetAudiences = [
    { value: 'kinder_jugendliche', label: 'Kinder & Jugendliche' },
    { value: 'bevoelkerung', label: 'Bevölkerung' },
    { value: 'unternehmen', label: 'Unternehmen' },
    { value: 'behoerden', label: 'Behörden' },
    { value: 'militaer', label: 'Militär' },
    { value: 'keine_angabe', label: 'Keine Angabe' },
  ];

  const handleOpen = () => {
    setOpen(true);
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      title: '',
      description: '',
      start_date: '',
      start_time: '',
      end_date: '',
      end_time: '',
      event_type: 'cybersecurity',
      target_audience: 'bevoelkerung',
      location: '',
      url: '',
      organizer: '',
      organizer_logo: '',
      is_all_day: false,
      reason: '',
    });
    setLogoFile(null);
    setLogoPreview(null);
    setError('');
    setSuccess(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Auto-hide tooltip when dialog opens
  useEffect(() => {
    if (open) {
      setShowTooltip(false);
    }
  }, [open]);

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  // Image upload functions
  const handleLogoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post('/images/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data.image_id;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  };

  const handleFaviconExtraction = async (url) => {
    try {
      const response = await axiosInstance.post('/images/favicon', { url });
      return response.data.image_id;
    } catch (error) {
      console.error('Error extracting favicon:', error);
      return null;
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      const errorMessage = 'Titel ist erforderlich';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }
    if (formData.title.trim().length > 200) {
      const errorMessage = 'Titel darf maximal 200 Zeichen lang sein';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }
    if (!formData.start_date) {
      const errorMessage = 'Startdatum ist erforderlich';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }
    if (!formData.start_time) {
      const errorMessage = 'Startzeit ist erforderlich';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }
    if (!formData.end_date) {
      const errorMessage = 'Enddatum ist erforderlich';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }
    if (!formData.end_time) {
      const errorMessage = 'Endzeit ist erforderlich';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }
    if (!formData.reason.trim()) {
      const errorMessage = 'Begründung ist erforderlich - erkläre, warum diese Veranstaltung hinzugefügt werden sollte';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }
    if (formData.reason.trim().length < 10) {
      const errorMessage = 'Begründung muss mindestens 10 Zeichen lang sein';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }
    if (formData.reason.trim().length > 1000) {
      const errorMessage = 'Begründung darf maximal 1000 Zeichen lang sein';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }
    if (!formData.target_audience) {
      const errorMessage = 'Zielgruppe ist erforderlich';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }

    // Validate date range
    const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
    const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);
    
    if (endDateTime <= startDateTime) {
      const errorMessage = 'Enddatum muss nach dem Startdatum liegen';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      return false;
    }

    // Validate URL if provided
    if (formData.url && formData.url.trim()) {
      try {
        new URL(formData.url);
      } catch {
        const errorMessage = 'Bitte gib eine gültige URL ein';
        setError(errorMessage);
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm()) {
        return;
      }

      setLoading(true);
      setError('');
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);

      // Handle logo upload (optional - don't fail if it doesn't work)
      let logoImageId = null;
      try {
        if (logoFile) {
          logoImageId = await handleLogoUpload(logoFile);
        } else if (formData.url && !logoFile) {
          // Try to extract favicon from URL
          logoImageId = await handleFaviconExtraction(formData.url);
        }
      } catch (logoError) {
        console.warn('Logo upload failed, continuing without logo:', logoError);
        // Continue without logo - this is not a critical error
      }

      const suggestionData = {
        title: formData.title.trim(),
        description: formData.description?.trim() || null,
        start_date: startDateTime.toISOString(),
        end_date: endDateTime.toISOString(),
        event_type: formData.event_type,
        target_audience: formData.target_audience,
        location: formData.location?.trim() || null,
        url: formData.url?.trim() || null,
        organizer: formData.organizer?.trim() || null,
        organizer_logo: logoImageId,
        show_logo: false, // Default to false for user suggestions
        is_all_day: formData.is_all_day,
        reason: formData.reason.trim(),
      };

      console.log('Sending event suggestion data:', suggestionData);
      await axiosInstance.post('/event-suggestions', suggestionData);
      
      // Close dialog immediately and show success snackbar
      handleClose();
      setSnackbar({
        open: true,
        message: 'Vielen Dank für die Einreichung des Events. Nach Prüfung und Freigabe wird das Event im Kalender angezeigt.',
        severity: 'success'
      });
      
    } catch (err) {
      console.error('Error submitting event suggestion:', err);
      const errorMessage = err.response?.data?.detail || 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut oder kontaktiere den Support.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <EventSuggestionIconContainer showTooltip={showTooltip}>
        <EventSuggestionIconOverlay
          onClick={() => {
            handleOpen();
            setShowTooltip(false); // Hide tooltip when opening dialog
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onTouchStart={() => {
            // On mobile, show tooltip briefly then hide it
            setShowTooltip(true);
            setTimeout(() => setShowTooltip(false), 2000);
          }}
        >
          <EventIcon sx={{ color: 'white', fontSize: 28 }} />
        </EventSuggestionIconOverlay>
        
        {showTooltip && (
          <EventSuggestionTooltip>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
              Veranstaltung vorschlagen
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Schlage eine neue Veranstaltung für den Kalender vor
            </Typography>
          </EventSuggestionTooltip>
        )}
      </EventSuggestionIconContainer>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '600px',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <EventIcon color="primary" />
          <Typography variant="h6" component="div">
            Veranstaltung vorschlagen
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titel der Veranstaltung *"
                value={formData.title}
                onChange={handleInputChange('title')}
                placeholder="z.B. Cybersecurity Workshop 2025"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={formData.description}
                onChange={handleInputChange('description')}
                multiline
                rows={3}
                placeholder="Beschreiben Sie die Veranstaltung..."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Startdatum *"
                type="date"
                value={formData.start_date}
                onChange={handleInputChange('start_date')}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Startzeit *"
                type="time"
                value={formData.start_time}
                onChange={handleInputChange('start_time')}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Enddatum *"
                type="date"
                value={formData.end_date}
                onChange={handleInputChange('end_date')}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Endzeit *"
                type="time"
                value={formData.end_time}
                onChange={handleInputChange('end_time')}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Veranstaltungstyp *</InputLabel>
                <Select
                  value={formData.event_type}
                  onChange={handleInputChange('event_type')}
                  label="Veranstaltungstyp *"
                >
                  {eventTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Zielgruppe *</InputLabel>
                <Select
                  value={formData.target_audience}
                  onChange={handleInputChange('target_audience')}
                  label="Zielgruppe *"
                >
                  {targetAudiences.map((audience) => (
                    <MenuItem key={audience.value} value={audience.value}>
                      {audience.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ort"
                value={formData.location}
                onChange={handleInputChange('location')}
                placeholder="z.B. Online, Berlin, München..."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Veranstalter/Organisator"
                value={formData.organizer}
                onChange={handleInputChange('organizer')}
                placeholder="z.B. Cyberlagebild Community, BSI, etc."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Organisator Logo
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ marginBottom: '8px' }}
                />
                {logoPreview && (
                  <Box sx={{ mt: 1 }}>
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      style={{ 
                        maxWidth: '100px', 
                        maxHeight: '100px', 
                        objectFit: 'contain',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                      }} 
                    />
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  Laden Sie ein Logo hoch oder lassen Sie es leer, um automatisch das Favicon der Website zu verwenden
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL (optional)"
                value={formData.url}
                onChange={handleInputChange('url')}
                placeholder="https://example.com"
                helperText="Link zur Veranstaltung oder Anmeldung"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Begründung *"
                value={formData.reason}
                onChange={handleInputChange('reason')}
                multiline
                rows={3}
                placeholder="Erkläre, warum diese Veranstaltung zum Cyberlagebild-Kalender hinzugefügt werden sollte..."
                required
                helperText="Diese Begründung hilft den Administratoren bei der Bewertung Deines Vorschlags"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Hinweis:</strong> Dein Vorschlag wird von den Administratoren überprüft und hinzugefügt oder abgelehnt.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleClose}
            startIcon={<CloseIcon />}
            disabled={loading}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            disabled={loading || success}
            sx={{ minWidth: 140 }}
          >
            {loading ? 'Wird gesendet...' : 'Vorschlag einreichen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ 
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EventSuggestionIcon;
